import { useEffect, useState } from "react";

import { useSelector } from "react-redux";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { useStatsigContext } from "../../elements-statsig-provider/utils";
import {
  FSV_HOST_GUIDE_BANNER_TYPE,
  HOST_HUB_MARKETING_BANNERS_DYNAMIC_CONFIG_NAME,
} from "../constants/host-hub-marketing-banners.consts";
import {
  findMarketingBannerConfig,
  hasUsFsvListing,
  isMarketingBannerDismissed,
  isMarketingBannerExpired,
  markMarketingBannerDismissed,
  markMarketingBannerViewed,
  MarketingBannerImpression,
  MarketingBannersConfigValue,
  parseMarketingBannerImpression,
} from "../utils/host-hub-marketing-banners.utils";

import useHostHubOwnerListings from "./useHostHubOwnerListings";

type LoadedImpression = {
  impression: MarketingBannerImpression | null;
  storageKey: string;
};

type StatsigDynamicConfigs = Record<
  string,
  (() => MarketingBannersConfigValue) | undefined
>;

/**
 * Controls visibility of the FSV Host Guide banner on the Host Hub, mirroring
 * mobile's callout logic. Shown to hosts with at least one active US FSV
 * listing while the `fsv` entry exists in the `host_hub_marketing_banners`
 * Statsig config. It expires `numExpirationDays` after the host first sees it,
 * and a dismissal hides it for `numDismissalDays` (for good if omitted).
 */
const useFSVHostGuideBanner = () => {
  const ssoId = useSelector(selectProfileSSOid);
  const storageKey = ssoId ? `hostHub:fsvHostGuide:impression:${ssoId}` : null;
  const { dynamicConfigs, initialized } = useStatsigContext();
  const { isLoading: isListingsLoading, listings } = useHostHubOwnerListings();

  // Tagged with the key it was read from, so a record loaded for a previous
  // account is never used (or overwritten) after `ssoId` changes.
  const [loadedImpression, setLoadedImpression] = useState<
    LoadedImpression | undefined
  >(undefined);
  // `undefined` means "not yet read for this key"; `null` means never seen.
  const impression =
    storageKey && loadedImpression?.storageKey === storageKey
      ? loadedImpression.impression
      : undefined;

  useEffect(() => {
    if (!storageKey) return;
    let storedImpression: MarketingBannerImpression | null;
    try {
      storedImpression = parseMarketingBannerImpression(
        window.localStorage.getItem(storageKey),
      );
    } catch {
      storedImpression = null;
    }
    setLoadedImpression({ impression: storedImpression, storageKey });
  }, [storageKey]);

  const persistImpression = (nextImpression: MarketingBannerImpression) => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(nextImpression));
    } catch {
      // Ignore: quota / private-mode failures shouldn't break the page.
    }
    setLoadedImpression({ impression: nextImpression, storageKey });
  };

  const bannerConfig = findMarketingBannerConfig({
    configValue: (dynamicConfigs as StatsigDynamicConfigs)?.[
      HOST_HUB_MARKETING_BANNERS_DYNAMIC_CONFIG_NAME
    ]?.(),
    type: FSV_HOST_GUIDE_BANNER_TYPE,
  });
  const now = Date.now();

  // Wait for Statsig, listings, and the stored impression to load.
  const isLoaded =
    initialized && !isListingsLoading && impression !== undefined;
  const isVisible = (() => {
    if (!isLoaded || bannerConfig === undefined || !hasUsFsvListing(listings)) {
      return false;
    }
    const bannerState = {
      config: bannerConfig,
      impression: impression ?? null,
      now,
    };
    return (
      !isMarketingBannerExpired(bannerState) &&
      !isMarketingBannerDismissed(bannerState)
    );
  })();

  // Start the expiration window the first time the banner is shown.
  useEffect(() => {
    if (!isVisible || impression) return;
    persistImpression(
      markMarketingBannerViewed({ impression: null, now: Date.now() }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- persistImpression is recreated each render
  }, [isVisible, impression]);

  const dismiss = () => {
    persistImpression(
      markMarketingBannerDismissed({
        impression: impression ?? null,
        now: Date.now(),
      }),
    );
  };

  return {
    // The matched `fsv` config entry; only set while the banner is visible,
    // so callers can't render it without one.
    callout: isVisible ? bannerConfig : undefined,
    dismiss,
    isVisible,
  };
};

export default useFSVHostGuideBanner;
